// packages/console/src/components/panels/ProbeGraphPanel.tsx
//
// Admin-only probe bit-distribution graph for a job (right column). It fetches
// the orchestrator's binned bits/frame envelope and renders it with uPlot:
//   - bitsMean line (primary) over a translucent bitsMax envelope so peaks read
//   - vertical cut lines at each detected scene cut
//   - a dashed CABA bits/sec target step track on a SECOND y-axis
//   - translucent bands over banding scenes where a deband would fire
//   - a cursor hover readout (t, bitsMean, bitsMax, active CABA target)
//   - an optional I/P/B view that re-fetches with frameTypes=1 when toggled on
//
// States are honest (R10): loading, error, 403 (admin only, should not normally
// render here), 429 (busy, retry), empty, loaded. Colors come from the design
// tokens; the chart palette is derived from the console's CSS variables.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import { Dot, Read } from "../Parts";
import { UPlotChart } from "../UPlotChart";
import { getJobProbeGraph, type ProbeGraph } from "../../api/jobs";
import { ApiError } from "../../api/http";
import { EM_DASH, fmtClock } from "../../lib/format";
import { cabaTrack, stepValueAt } from "../../lib/probeGraph";

const CHART_HEIGHT = 190;

// resolveVar reads a design token off the document root so the chart palette
// tracks the imported design CSS rather than hardcoding hex. In a non-DOM
// context (or before styles load) it returns the given fallback.
function resolveVar(name: string, fallback: string): string {
  if (typeof window === "undefined" || !window.getComputedStyle) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

// PanelState is the panel's fetch lifecycle. 403/429 are split out from a
// generic error so each shows the right affordance (admin-only vs retry).
type PanelState =
  | { kind: "loading" }
  | { kind: "forbidden" }
  | { kind: "busy" }
  | { kind: "error"; message: string }
  | { kind: "empty" }
  | { kind: "loaded"; graph: ProbeGraph };

// classifyError maps a thrown ApiError to a panel state: 403 -> forbidden,
// 429 -> busy, everything else -> a generic error carrying the message.
function classifyError(err: unknown): PanelState {
  if (err instanceof ApiError) {
    if (err.status === 403) return { kind: "forbidden" };
    if (err.status === 429) return { kind: "busy" };
    return { kind: "error", message: err.message };
  }
  return { kind: "error", message: err instanceof Error ? err.message : "request failed" };
}

export function ProbeGraphPanel({ jobId, filename }: { jobId: string; filename?: string }) {
  const [state, setState] = useState<PanelState>({ kind: "loading" });
  // showTypes drives the I/P/B view. Turning it on triggers a frameTypes=1
  // refetch (the untoggled fetch omits bitsByType to keep the payload small).
  const [showTypes, setShowTypes] = useState(false);
  // reloadTick lets the retry button force a fresh fetch of the same job.
  const [reloadTick, setReloadTick] = useState(0);

  const load = useCallback(
    (signal: AbortSignal, frameTypes: boolean) => {
      setState({ kind: "loading" });
      getJobProbeGraph(jobId, { frameTypes }, signal)
        .then((graph) => {
          if (signal.aborted) return;
          // Treat a graph with no plottable points as empty even if the server
          // reported a non-zero count (a partial/aborted probe can carry a
          // header count with empty arrays); render honestly rather than an
          // axis-only chart claiming success (R10).
          const hasPoints = graph.bins.count > 0 && graph.bins.t.length > 0;
          setState(hasPoints ? { kind: "loaded", graph } : { kind: "empty" });
        })
        .catch((err) => {
          if (signal.aborted) return;
          setState(classifyError(err));
        });
    },
    [jobId],
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal, showTypes);
    return () => ac.abort();
  }, [load, showTypes, reloadTick]);

  const onRetry = useCallback(() => setReloadTick((n) => n + 1), []);

  // No outer Panel: this body mounts inside the FileTranscoder bottom-center
  // Panel (the "Probe graph" tab), which supplies the frame and title.
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: "11px 14px",
        overflow: "hidden",
      }}
    >
      {state.kind === "loading" ? (
        <Note>Loading probe graph...</Note>
      ) : state.kind === "forbidden" ? (
        <Note>Probe graph is available to workspace admins only.</Note>
      ) : state.kind === "busy" ? (
        <BusyRetry onRetry={onRetry} />
      ) : state.kind === "error" ? (
        <Note tone="var(--err)">Could not load the probe graph: {state.message}</Note>
      ) : state.kind === "empty" ? (
        <Note>No probe data for this job yet.</Note>
      ) : (
        <GraphView
          graph={state.graph}
          filename={filename}
          showTypes={showTypes}
          onToggleTypes={setShowTypes}
        />
      )}
    </div>
  );
}

// Note is the shared small-text state row (loading/empty/error/forbidden).
function Note({ children, tone }: { children: React.ReactNode; tone?: string }) {
  return <div style={{ font: "var(--t-body-sm)", color: tone ?? "var(--fg4)" }}>{children}</div>;
}

// BusyRetry renders the 429 state: the probe graph is being computed, with a
// button to retry the fetch once it should be ready.
function BusyRetry({ onRetry }: { onRetry: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Note>Probe graph busy. It is being computed for this job.</Note>
      <button
        className="ae-b"
        onClick={onRetry}
        style={{
          alignSelf: "flex-start",
          font: "600 10px var(--font-sans)",
          letterSpacing: ".09em",
          textTransform: "uppercase",
          padding: "6px 12px",
          borderRadius: "var(--r-xs)",
          cursor: "pointer",
          color: "var(--fg1)",
          background: "transparent",
          border: "1px solid var(--line-strong)",
        }}
      >
        Retry
      </button>
    </div>
  );
}

// HoverInfo is the cursor readout payload: the sample under the cursor.
interface HoverInfo {
  t: number;
  bitsMean: number | null;
  bitsMax: number | null;
  caba: number | null;
}

function GraphView({
  graph,
  filename,
  showTypes,
  onToggleTypes,
}: {
  graph: ProbeGraph;
  filename?: string;
  showTypes: boolean;
  onToggleTypes: (v: boolean) => void;
}) {
  const [hover, setHover] = useState<HoverInfo | null>(null);
  // Keep the latest hover push in a ref so the plugin's setCursor closure (built
  // once inside the memoized options) always reads through to current state.
  const setHoverRef = useRef(setHover);
  setHoverRef.current = setHover;

  // The live uPlot instance, captured on (re)build, drives the zoom controls.
  const plotRef = useRef<uPlot | null>(null);
  const xRange = useCallback((): [number, number] | null => {
    const u = plotRef.current;
    const xs = u?.data?.[0] as number[] | undefined;
    if (!u || !xs || xs.length < 2) return null;
    return [xs[0], xs[xs.length - 1]];
  }, []);
  // zoomBy scales the visible x-span about its center; factor < 1 zooms in,
  // > 1 zooms out, clamped to the full data range.
  const zoomBy = useCallback(
    (factor: number) => {
      const u = plotRef.current;
      const full = xRange();
      if (!u || !full) return;
      const [f0, f1] = full;
      const min = u.scales.x.min ?? f0;
      const max = u.scales.x.max ?? f1;
      const center = (min + max) / 2;
      const span = Math.min((max - min) * factor, f1 - f0);
      let lo = center - span / 2;
      let hi = center + span / 2;
      if (lo < f0) [lo, hi] = [f0, f0 + span];
      if (hi > f1) [lo, hi] = [f1 - span, f1];
      u.setScale("x", { min: lo, max: hi });
    },
    [xRange],
  );
  const fit = useCallback(() => {
    const u = plotRef.current;
    const full = xRange();
    if (u && full) u.setScale("x", { min: full[0], max: full[1] });
  }, [xRange]);

  const hasTypes = Boolean(graph.bins.bitsByType);
  // typedView is the single derived flag every render path keys on: the user
  // asked for the I/P/B view AND the data actually carries the per-type series.
  // If the toggle is on but the (still-loading) fetch has no types yet, all
  // paths fall back to the envelope so data/options/cursor never disagree.
  const typedView = showTypes && hasTypes;
  const caba = useMemo(
    () => cabaTrack(graph.cabaTarget.steps, graph.bins.t),
    [graph.cabaTarget.steps, graph.bins.t],
  );

  // Palette from the ProbePass design mock: frame types read cyan / blue /
  // violet (I / P / B), the mean line takes the P blue, and peaks sit in a
  // translucent blue envelope. The rest tracks the console's CSS tokens.
  const palette = useMemo(
    () => ({
      grid: "rgba(255,255,255,0.06)",
      axis: resolveVar("--fg4", "rgba(255,255,255,0.46)"),
      mean: "#4F86F7",
      max: "rgba(79,134,247,0.20)",
      caba: resolveVar("--warn", "#FFC62B"),
      cut: "rgba(255,255,255,0.30)",
      band: "rgba(245,165,36,0.12)",
      i: "#22D3EE",
      p: "#4F86F7",
      b: "#A78BFA",
    }),
    [],
  );

  // Honest summary readouts from the binned envelope + probe metadata. bitsMean
  // and bitsMax are bits per frame, so an instantaneous rate is bits x fps.
  const summary = useMemo(() => {
    const fps = graph.meta.fps || 25;
    const means = graph.bins.bitsMean;
    const maxes = graph.bins.bitsMax;
    let meanSum = 0;
    let meanN = 0;
    for (const v of means) {
      if (v != null) {
        meanSum += v;
        meanN += 1;
      }
    }
    let peak = 0;
    for (const v of maxes) if (v != null && v > peak) peak = v;
    const meanBits = meanN ? meanSum / meanN : 0;
    return {
      meanMbps: ((meanBits * fps) / 1e6).toFixed(1),
      peakMbps: ((peak * fps) / 1e6).toFixed(1),
      frames: graph.meta.totalFrames,
      cq: graph.meta.probeParams.cq,
    };
  }, [graph.bins.bitsMean, graph.bins.bitsMax, graph.meta]);

  // Aligned data: x is the bin times; the y series depend on the view. The
  // typed I/P/B view replaces the envelope with three colored per-type lines.
  const data: uPlot.AlignedData = useMemo(() => {
    const t = graph.bins.t;
    if (typedView && graph.bins.bitsByType) {
      const bt = graph.bins.bitsByType;
      return [t, bt.I, bt.P, bt.B, caba];
    }
    return [t, graph.bins.bitsMax, graph.bins.bitsMean, caba];
  }, [graph.bins, typedView, caba]);

  // cutsPlugin draws a vertical line at each cut's time, mapped through the x
  // scale to a canvas pixel. bandsPlugin shades banding scenes where a deband
  // would fire. Both run in the draw hook against the plot's canvas context.
  const drawPlugin = useMemo<uPlot.Plugin>(() => {
    const cuts = graph.cuts;
    const scenes = graph.banding.scenes;
    return {
      hooks: {
        draw: (u: uPlot) => {
          const ctx = u.ctx;
          const top = u.bbox.top;
          const bottom = u.bbox.top + u.bbox.height;
          ctx.save();
          // Banding bands first (behind the cut lines and series).
          ctx.fillStyle = palette.band;
          for (const s of scenes) {
            if (!s.debandWouldFire) continue;
            const x0 = u.valToPos(s.startSeconds, "x", true);
            const x1 = u.valToPos(s.endSeconds, "x", true);
            ctx.fillRect(x0, top, Math.max(1, x1 - x0), bottom - top);
          }
          // Cut lines on top of the shading.
          ctx.strokeStyle = palette.cut;
          ctx.lineWidth = 1;
          for (const c of cuts) {
            const x = u.valToPos(c.seconds, "x", true);
            ctx.beginPath();
            ctx.moveTo(x, top);
            ctx.lineTo(x, bottom);
            ctx.stroke();
          }
          ctx.restore();
        },
      },
    };
  }, [graph.cuts, graph.banding.scenes, palette.band, palette.cut]);

  // cursorPlugin pushes the sample under the cursor up to React for the readout.
  const cursorPlugin = useMemo<uPlot.Plugin>(() => {
    const cabaSteps = graph.cabaTarget.steps;
    return {
      hooks: {
        setCursor: (u: uPlot) => {
          const idx = u.cursor.idx;
          if (idx === null || idx === undefined) {
            setHoverRef.current(null);
            return;
          }
          const t = (u.data[0] as number[])[idx];
          // A stale idx from a data-length change mid-transition can land past
          // the current array; skip the readout rather than reporting t as 0:00.
          if (t === undefined) {
            setHoverRef.current(null);
            return;
          }
          // Envelope view lays out [t, bitsMax, bitsMean, caba]; the typed view
          // is [t, I, P, B, caba], which carries no mean/max, so the readout
          // shows an em dash for those (honest, R10) while t and CABA still read.
          const maxArr = typedView ? undefined : (u.data[1] as (number | null)[]);
          const meanArr = typedView ? undefined : (u.data[2] as (number | null)[]);
          setHoverRef.current({
            t,
            bitsMean: meanArr ? meanArr[idx] ?? null : null,
            bitsMax: maxArr ? maxArr[idx] ?? null : null,
            caba: stepValueAt(cabaSteps, t),
          });
        },
      },
    };
  }, [graph.cabaTarget.steps, typedView]);

  const options = useMemo<Omit<uPlot.Options, "width" | "height">>(() => {
    const meanStroke = palette.mean;
    const series: uPlot.Series[] = [{}];
    if (typedView) {
      series.push(
        { label: "I", stroke: palette.i, width: 1.5, scale: "bits", points: { show: false } },
        { label: "P", stroke: palette.p, width: 1.5, scale: "bits", points: { show: false } },
        { label: "B", stroke: palette.b, width: 1.5, scale: "bits", points: { show: false } },
      );
    } else {
      series.push(
        // bitsMax envelope: a lighter translucent fill so peaks stand out.
        { label: "Max", stroke: palette.max, fill: palette.max, width: 1, scale: "bits", points: { show: false } },
        // bitsMean primary line.
        { label: "Mean", stroke: meanStroke, width: 1.75, scale: "bits", points: { show: false } },
      );
    }
    // CABA target track on the second y-axis (bits/sec), dashed step line. The
    // built-in stepped path builder holds each target level across its step.
    const stepped = uPlot.paths.stepped?.({ align: 1 });
    series.push({
      label: "CABA",
      stroke: palette.caba,
      width: 1.25,
      dash: [4, 3],
      scale: "bps",
      points: { show: false },
      ...(stepped ? { paths: stepped } : {}),
    });

    return {
      // Legend off: the hover readout below is the house-styled substitute.
      legend: { show: false },
      // Drag a horizontal range to zoom in; double-click resets to full. The
      // explicit Zoom/Fit buttons drive the same x-scale for discoverability.
      cursor: { drag: { x: true, y: false }, focus: { prox: 16 } },
      scales: {
        x: { time: false },
        bits: { auto: true },
        bps: { auto: true },
      },
      axes: [
        {
          scale: "x",
          stroke: palette.axis,
          grid: { stroke: palette.grid, width: 1 },
          ticks: { stroke: palette.grid, width: 1 },
          font: "10px var(--font-mono, monospace)",
          values: (_u, splits) => splits.map((s) => fmtClock(s)),
        },
        {
          scale: "bits",
          stroke: palette.axis,
          grid: { stroke: palette.grid, width: 1 },
          ticks: { stroke: palette.grid, width: 1 },
          font: "10px var(--font-mono, monospace)",
        },
        {
          scale: "bps",
          side: 1,
          stroke: palette.caba,
          grid: { show: false },
          ticks: { stroke: palette.grid, width: 1 },
          font: "10px var(--font-mono, monospace)",
        },
      ],
      series,
      plugins: [drawPlugin, cursorPlugin],
    };
  }, [palette, typedView, drawPlugin, cursorPlugin]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, height: "100%", minHeight: 0 }}>
      {/* Header: source + probe status + series legend (design ProbePass chrome). */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "none" }}>
        <Dot tone="ok" />
        <span
          style={{
            font: "400 12px var(--font-mono)",
            color: "#fff",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 340,
          }}
        >
          {filename || "source"}
        </span>
        <span style={{ font: "var(--t-micro)", color: "var(--fg3)", whiteSpace: "nowrap" }}>
          CQ {summary.cq} probe complete
        </span>
        <div style={{ flex: 1 }} />
        <Legend showTypes={typedView} palette={palette} />
      </div>

      {/* Stat readouts + the I/P/B toggle (design ProbePass stat row). The toggle
          drives a frameTypes=1 refetch; the note stays honest when the loaded
          graph carries no per-type data (R10). */}
      <div style={{ display: "flex", alignItems: "center", gap: 20, flex: "none" }}>
        <Read value={summary.meanMbps} unit="Mb/s" label="Mean rate" size={16} />
        <Read value={summary.peakMbps} unit="Mb/s" label="Peak frame" size={16} />
        <Read value={(summary.frames / 1000).toFixed(1) + "k"} label="Frames" size={16} />
        <div style={{ flex: 1 }} />
        {showTypes && !typedView && (
          <span style={{ font: "var(--t-micro)", color: "var(--fg4)", whiteSpace: "nowrap" }}>
            no per-type data
          </span>
        )}
        {/* Zoom controls: drive the same x-scale as drag-zoom / double-click. */}
        <div style={{ display: "flex", gap: 6 }}>
          <ZoomBtn label="−" title="Zoom out" onClick={() => zoomBy(2)} />
          <ZoomBtn label="+" title="Zoom in" onClick={() => zoomBy(0.5)} />
          <ZoomBtn label="Fit" title="Fit the whole title" onClick={fit} />
        </div>
        <Toggle
          on={showTypes}
          label="I / P / B"
          onClick={() => onToggleTypes(!showTypes)}
          title="Switch to the per-frame-type (I/P/B) view"
        />
      </div>

      <UPlotChart
        options={options}
        data={data}
        height={CHART_HEIGHT}
        onCreate={(u) => {
          plotRef.current = u;
        }}
      />

      {/* Cursor hover readout: t, bitsMean, bitsMax, and the active CABA step. */}
      <HoverReadout hover={hover} />
    </div>
  );
}

// Toggle is a small pill button matching the console's tab chips.
function Toggle({ on, label, onClick, title }: { on: boolean; label: string; onClick: () => void; title: string }) {
  return (
    <button
      className="ae-b"
      onClick={onClick}
      title={title}
      style={{
        font: "600 10px var(--font-sans)",
        letterSpacing: ".09em",
        textTransform: "uppercase",
        padding: "5px 10px",
        borderRadius: "var(--r-xs)",
        cursor: "pointer",
        whiteSpace: "nowrap",
        background: on ? "var(--blue-tint)" : "transparent",
        border: `1px solid ${on ? "var(--blue-500)" : "var(--line-strong)"}`,
        color: on ? "var(--blue-300)" : "var(--fg2)",
      }}
    >
      {label}
    </button>
  );
}

// ZoomBtn is a compact bordered control for the zoom/fit actions, matching the
// design mock's uppercase button chrome.
function ZoomBtn({ label, title, onClick }: { label: string; title: string; onClick: () => void }) {
  return (
    <button
      className="ae-b"
      onClick={onClick}
      title={title}
      style={{
        minWidth: 26,
        font: "600 10px var(--font-sans)",
        letterSpacing: ".06em",
        textTransform: "uppercase",
        padding: "5px 8px",
        borderRadius: "var(--r-xs)",
        cursor: "pointer",
        background: "transparent",
        border: "1px solid var(--line-strong)",
        color: "var(--fg1)",
      }}
    >
      {label}
    </button>
  );
}

// Legend labels the visible series with their palette colors (the uPlot legend
// is off so the house look stays consistent).
function Legend({ showTypes, palette }: { showTypes: boolean; palette: Record<string, string> }) {
  const items = showTypes
    ? [
        { c: palette.i, l: "I" },
        { c: palette.p, l: "P" },
        { c: palette.b, l: "B" },
        { c: palette.caba, l: "CABA" },
      ]
    : [
        { c: palette.mean, l: "Mean" },
        { c: palette.max, l: "Max" },
        { c: palette.caba, l: "CABA" },
      ];
  return (
    <div style={{ display: "flex", gap: 10 }}>
      {items.map((it) => (
        <span key={it.l} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 9, height: 2, background: it.c, display: "inline-block" }} />
          <span style={{ font: "500 9px var(--font-sans)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--fg3)" }}>
            {it.l}
          </span>
        </span>
      ))}
    </div>
  );
}

// HoverReadout shows the sample under the cursor, or a prompt when idle. Values
// render honestly (em dash) when a series has no value at the cursor.
function HoverReadout({ hover }: { hover: HoverInfo | null }) {
  const cell = (label: string, value: string) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span style={{ font: "500 9px var(--font-sans)", letterSpacing: ".13em", textTransform: "uppercase", color: "var(--fg3)" }}>
        {label}
      </span>
      <span style={{ font: "400 12px var(--font-mono)", color: "#fff", fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
  const bits = (v: number | null) => (v === null ? EM_DASH : Math.round(v).toLocaleString());
  return (
    <div style={{ display: "flex", gap: 20, padding: "8px 2px", borderTop: "1px solid var(--line)" }}>
      {hover ? (
        <>
          {cell("t", fmtClock(hover.t))}
          {cell("Mean", bits(hover.bitsMean))}
          {cell("Max", bits(hover.bitsMax))}
          {cell("CABA b/s", bits(hover.caba))}
        </>
      ) : (
        <span style={{ font: "var(--t-body-sm)", color: "var(--fg4)" }}>
          Hover to read a sample · drag to zoom, double-click to reset.
        </span>
      )}
    </div>
  );
}
